import { createFileRoute } from '@tanstack/react-router';
import { Button, Form, Input, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { profileQuickLinksQueryOptions } from '../../../../utils/queryOptions';
import { apiV1 } from '../../../../utils/apiClient';
import PageLoader from '../../../../microcomponents/PageLoader';
import { z } from 'zod';
import posthog from 'posthog-js';
import PlanGate from '../../../../components/PlanGate';
import { NButton } from '@next-is-free-monorepo/nif-web-components';
import { PlusSignIcon } from '@hugeicons/core-free-icons';

export const Route = createFileRoute('/_authenticated/customize/quick-links')({
  component: RouteComponent,
  pendingComponent: () => <PageLoader />
});

function RouteComponent() {

  const queryClient = useQueryClient();
  const [quickLinksForm] = Form.useForm();
  const [touched, setTouched] = useState(false);

  const quickLinks = useSuspenseQuery(profileQuickLinksQueryOptions());

  const saveQuickLinks = useMutation({
    mutationFn: async () => {
      await apiV1.put('/shop/profile/quick-links', quickLinksForm.getFieldsValue());
    },
    onError: (error) => {
      if (error.message) {
        message.error(error.message);
      }
    },
    onSuccess: async () => {
      setTouched(false);
      posthog.capture('profile_quick_links_saved');
      queryClient.invalidateQueries({ queryKey: ['profile-quick-links'] });
      message.success('Le modifiche sono state pubblicate');
    }
  });

  async function onFormChange(_: any, values: {
    phone_numbers: { label: string, value: string }[] | undefined,
    links: { label: string, value: string }[] | undefined
  }) {
    setTouched(true);
    queryClient.setQueryData(['profile-quick-links'], (oldData: any) => {
      return values;
    });
  }

  return (
    <div>
      <Form initialValues={quickLinks.data} form={quickLinksForm} onValuesChange={onFormChange} onFinish={() => {
        saveQuickLinks.mutate();
      }}>
        <div className={'py-5'}>
          <p className={'font-medium mb-2'}>Links</p>
          <Form.List name="links">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 15
                    }}
                    key={key}
                  >
                    <Form.Item
                      noStyle
                      {...restField}
                      name={[name, 'label']}
                      rules={[
                        {
                          required: true,
                          message: 'Inserire un etichetta',
                          validateTrigger: 'onChange'
                        }
                      ]}
                    >
                      <Input
                        autoComplete="off"
                        maxLength={30}
                        style={{ width: '100%' }}
                        placeholder="Etichetta"
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      {...restField}
                      name={[name, 'value']}
                      rules={[
                        {
                          required: true,
                          message: 'Inserire un link valido',
                          validator: (_, value) => {
                            //validate url with zod
                            if (z.string().url().safeParse(value).success) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(new Error('Inserire un link valido'));
                            }
                          }
                        }
                      ]}
                    >
                      <Input
                        type={'url'}
                        min={1}
                        max={5000}
                        style={{ width: '100%' }}
                        placeholder="https://www.example.com"
                      />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </div>
                ))}
                <Form.Item>
                  <PlanGate hideMask featureKey="quickLinks">
                    <NButton color={'tertiary'} type={'button'}
                             onClick={() => add()} size={'small'} hugeIcon={PlusSignIcon}
                             className={'w-full'}
                    >
                      Aggiungi un link
                    </NButton>
                  </PlanGate>
                </Form.Item>
              </>
            )}
          </Form.List>
        </div>

        <div className={'py-5 border-t'}>
          <p className={'font-medium mb-2'}>Numeri di telefono</p>
          <Form.List name="phone_numbers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 15
                    }}
                    key={key}
                  >
                    <Form.Item
                      noStyle
                      {...restField}
                      name={[name, 'label']}
                      rules={[
                        {
                          required: true,
                          message: 'Inserire un\'etichetta'
                        }
                      ]}
                    >
                      <Input
                        autoComplete="off"
                        maxLength={30}
                        style={{ width: '100%' }}
                        placeholder="Nome del premio"
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      {...restField}
                      name={[name, 'value']}
                      rules={[
                        {
                          required: true,
                          message: 'Inserire un numero di telefono valido'
                        }
                      ]}
                    >
                      <Input
                        style={{ width: '100%' }}
                        placeholder="323 123 4567"
                      />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </div>
                ))}
                <Form.Item>
                  <PlanGate hideMask featureKey="quickLinks">
                    <NButton color={'tertiary'} type={'button'}
                             onClick={() => add()} size={'small'} hugeIcon={PlusSignIcon}
                             className={'w-full'}
                    >
                      Aggiungi un numero di telefono
                    </NButton>
                  </PlanGate>
                </Form.Item>
              </>
            )}
          </Form.List>
        </div>
      </Form>
      <div className={'sticky bottom-10 md:bottom-0 w-full bg-white pb-4'}>
        <NButton
          disabled={!touched}
          loading={saveQuickLinks.isPending}
          onClick={() => {
            quickLinksForm.submit();
          }}
          className={'w-full'}
        >
          Salva le modifiche
        </NButton>
      </div>
    </div>
  );
}
